// API client functions for framework configuration endpoints.

import type { ApiClient } from '@/lib/api/client'

// --- Shared versioned component types ---

/** Version envelope returned by list-versions endpoints. */
export interface VersionSummary {
  id: string
  version_number: number
  is_active: boolean
  activated_by: string | null
  activated_at: string | null
  confirmed_by: string | null
  confirmed_at: string | null
  created_by: string
  created_at: string
}

/** Response from activate endpoints. Status is "activated" for immediate
 *  activation or "pending_confirmation" when confirmation gates are enabled. */
export interface ActivationResponse {
  status: 'activated' | 'pending_confirmation'
  message?: string
}

/** Derived status from version fields. */
export type VersionStatus = 'draft' | 'active' | 'retired'

/** Derive a display status from a VersionSummary. */
export function deriveVersionStatus(version: VersionSummary): VersionStatus {
  if (version.is_active) return 'active'
  if (version.activated_at) return 'retired'
  return 'draft'
}

/** A single level used by both likelihood and impact table versions. */
export interface Level {
  id: string
  level_number: number
  label: string
  description?: string
}

/** Level input for create/edit requests (no id). */
export interface LevelInput {
  level_number: number
  label: string
  description?: string
}

// --- Scoring Grid ---

export interface ScoringGrid {
  target_likelihood_level_count: number
  target_impact_level_count: number
  updated_by: string
  updated_at: string
}

export interface UpdateScoringGridRequest {
  target_likelihood_level_count: number
  target_impact_level_count: number
}

export function scoringGridApi(api: ApiClient) {
  return {
    get: (): Promise<ScoringGrid> => api.get('/framework-config/scoring-grid'),

    update: (data: UpdateScoringGridRequest): Promise<ScoringGrid> =>
      api.put('/framework-config/scoring-grid', data),
  }
}

// --- Assessment Thresholds ---

export interface AssessmentThresholds {
  self_assessed_design_days: number
  self_assessed_operating_days: number
  independent_design_days: number
  independent_operating_days: number
}

export type UpdateAssessmentThresholdsRequest = AssessmentThresholds

export function assessmentThresholdsApi(api: ApiClient) {
  return {
    get: (): Promise<AssessmentThresholds> => api.get('/framework-config/assessment-thresholds'),

    update: (data: UpdateAssessmentThresholdsRequest): Promise<AssessmentThresholds> =>
      api.put('/framework-config/assessment-thresholds', data),
  }
}

// --- Evidence Tags ---

export interface EvidenceTag {
  id: string
  tag_name: string
  description?: string
}

export interface CreateEvidenceTagRequest {
  tag_name: string
  description?: string
}

export interface UpdateEvidenceTagRequest {
  tag_name?: string
  description?: string
}

export function evidenceTagsApi(api: ApiClient) {
  return {
    list: (): Promise<EvidenceTag[]> => api.get('/framework-config/evidence-tags'),

    create: (data: CreateEvidenceTagRequest): Promise<EvidenceTag> =>
      api.post('/framework-config/evidence-tags', data),

    update: (id: string, data: UpdateEvidenceTagRequest): Promise<EvidenceTag> =>
      api.put(`/framework-config/evidence-tags/${id}`, data),

    remove: (id: string): Promise<void> => api.del(`/framework-config/evidence-tags/${id}`),
  }
}

// --- Likelihood ---

export interface ActiveLikelihood {
  version_id: string
  version_number: number
  activated_at: string
  levels: Level[]
}

export function likelihoodApi(api: ApiClient) {
  return {
    getActive: (): Promise<ActiveLikelihood> => api.get('/framework-config/likelihood'),

    listVersions: (): Promise<VersionSummary[]> => api.get('/framework-config/likelihood/versions'),

    getVersion: (versionId: string): Promise<ActiveLikelihood> =>
      api.get(`/framework-config/likelihood/versions/${versionId}`),

    createVersion: (levels: LevelInput[]): Promise<VersionSummary> =>
      api.post('/framework-config/likelihood/versions', { levels }),

    editVersion: (versionId: string, levels: LevelInput[]): Promise<void> =>
      api.put(`/framework-config/likelihood/versions/${versionId}`, { levels }),

    activate: (versionId: string): Promise<ActivationResponse> =>
      api.post(`/framework-config/likelihood/versions/${versionId}/activate`),
  }
}

// --- Impact Tables ---

export interface ImpactTable {
  id: string
  name: string
  description?: string
  active_version_id: string | null
  active_version_num: number | null
  created_at: string
}

export interface CreateImpactTableRequest {
  name: string
  description?: string
}

/** Active version response for impact table (same shape as likelihood). */
export interface ActiveImpactTableVersion {
  version_id: string
  version_number: number
  activated_at: string | null
  activated_by: string | null
  levels: Level[]
}

export interface UpdateImpactTableRequest {
  name: string
  description?: string
}

export function impactTablesApi(api: ApiClient) {
  return {
    list: (): Promise<ImpactTable[]> => api.get('/framework-config/impact-tables'),

    create: (data: CreateImpactTableRequest): Promise<ImpactTable> =>
      api.post('/framework-config/impact-tables', data),

    update: (tableId: string, data: UpdateImpactTableRequest): Promise<ImpactTable> =>
      api.put(`/framework-config/impact-tables/${tableId}`, data),

    deactivate: (tableId: string): Promise<void> =>
      api.post(`/framework-config/impact-tables/${tableId}/deactivate`),

    listVersions: (tableId: string): Promise<VersionSummary[]> =>
      api.get(`/framework-config/impact-tables/${tableId}/versions`),

    getVersion: (tableId: string, versionId: string): Promise<ActiveImpactTableVersion> =>
      api.get(`/framework-config/impact-tables/${tableId}/versions/${versionId}`),

    createVersion: (tableId: string, levels: LevelInput[]): Promise<VersionSummary> =>
      api.post(`/framework-config/impact-tables/${tableId}/versions`, { levels }),

    editVersion: (tableId: string, versionId: string, levels: LevelInput[]): Promise<void> =>
      api.put(`/framework-config/impact-tables/${tableId}/versions/${versionId}`, { levels }),

    activate: (tableId: string, versionId: string): Promise<ActivationResponse> =>
      api.post(`/framework-config/impact-tables/${tableId}/versions/${versionId}/activate`),
  }
}

// --- Taxonomy ---

export interface TaxonomyNode {
  id: string
  parent_id: string | null
  path: string
  depth: number
  name: string
  description?: string
}

export interface ActiveTaxonomy {
  version_id: string
  version_number: number
  activated_at: string
  activated_by: string | null
  nodes: TaxonomyNode[]
}

export interface TaxonomyNodeInput {
  parent_path?: string
  name: string
  description?: string
}

export function taxonomyApi(api: ApiClient) {
  return {
    getActive: (): Promise<ActiveTaxonomy> => api.get('/framework-config/taxonomy'),

    listVersions: (): Promise<VersionSummary[]> => api.get('/framework-config/taxonomy/versions'),

    createVersion: (nodes: TaxonomyNodeInput[]): Promise<VersionSummary> =>
      api.post('/framework-config/taxonomy/versions', { nodes }),

    getVersion: (versionId: string): Promise<ActiveTaxonomy> =>
      api.get(`/framework-config/taxonomy/versions/${versionId}`),

    editVersion: (versionId: string, nodes: TaxonomyNodeInput[]): Promise<void> =>
      api.put(`/framework-config/taxonomy/versions/${versionId}`, { nodes }),

    activate: (versionId: string): Promise<ActivationResponse> =>
      api.post(`/framework-config/taxonomy/versions/${versionId}/activate`),

    confirm: (versionId: string): Promise<void> =>
      api.post(`/framework-config/taxonomy/versions/${versionId}/confirm`),
  }
}

// --- Risk Appetite ---

export interface AppetiteCell {
  likelihood_level: number
  impact_level: number
}

export interface AppetiteDefinition {
  id: string
  taxonomy_node_id: string
  taxonomy_node_name: string
  taxonomy_node_path: string
  statement: string | null
  permitted_cells: AppetiteCell[]
  created_by: string
  activated_at: string
}

export interface SetAppetiteRequest {
  permitted_cells: AppetiteCell[]
  statement?: string | null
}

export function appetiteApi(api: ApiClient) {
  return {
    list: (): Promise<AppetiteDefinition[]> => api.get('/framework-config/appetite'),

    getByNode: (nodeId: string): Promise<AppetiteDefinition> =>
      api.get(`/framework-config/appetite/${nodeId}`),

    set: (nodeId: string, data: SetAppetiteRequest): Promise<AppetiteDefinition> =>
      api.put(`/framework-config/appetite/${nodeId}`, data),

    remove: (nodeId: string): Promise<void> => api.del(`/framework-config/appetite/${nodeId}`),
  }
}
